"use client";

import { FeatherBellRing } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { RegisteredUnitFormValues } from "@/components/registered-units/RegisteredUnitFormDialog";
import { RegisteredUnitFormDialog } from "@/components/registered-units/RegisteredUnitFormDialog";
import type {
  RegisteredUnit,
  RegisteredUnitCopy,
} from "@/components/registered-units/RegisteredUnitTable";
import { RegisteredUnitTable } from "@/components/registered-units/RegisteredUnitTable";
import {
  useCreateFireAlarmCallPointCommandControllerCreate,
  useListFireAlarmCallPointsQueryControllerList,
  useUpdateFireAlarmCallPointCommandControllerUpdate,
} from "@/generated/fire-alarm-call-points/fire-alarm-call-points";

const COPY: RegisteredUnitCopy = {
  title: "Fire Alarm Call Points",
  description:
    "Register each call point so the weekly test can rotate through them",
  addLabel: "Add Call Point",
  emptyTitle: "No fire alarm call points yet",
  emptyBody:
    "Register each manual call point and its location. The weekly test covers one at a time, rotating so every device is checked.",
  emptyIcon: <FeatherBellRing />,
  itemNoun: "call points",
  errorMessage: "Error loading fire alarm call points. Please try again.",
};

const FORM_COPY = {
  // Call points "can be anywhere", so unlike extinguishers they may be
  // registered without a room.
  roomRequired: false,
  unitLabel: "Call Point",
  namePlaceholder: "e.g., Call Point 1 — Main Entrance",
  notesPlaceholder: "e.g., Break-glass, tested with a plastic element",
  noRoomsHelpText: "",
};

export function FireAlarmCallPointTable({ nurseryId }: { nurseryId: string }) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<RegisteredUnit | null>(null);

  const {
    data: unitsData,
    isLoading,
    error,
  } = useListFireAlarmCallPointsQueryControllerList(nurseryId, {
    includeInactive: true,
  });

  const units = useMemo(() => unitsData?.data ?? [], [unitsData]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].includes("/fire-alarm-call-points"),
      refetchType: "all",
    });
  };

  const { mutate: createUnit, isPending: isCreating } =
    useCreateFireAlarmCallPointCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Call Point added successfully");
          await invalidate();
          setDialogOpen(false);
          setEditingUnit(null);
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error
              ? e.message
              : "Failed to add fire alarm call point",
          ),
      },
    });

  const { mutate: updateUnit, isPending: isUpdating } =
    useUpdateFireAlarmCallPointCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          await invalidate();
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error
              ? e.message
              : "Failed to update fire alarm call point",
          ),
      },
    });

  const handleToggleActive = (unit: RegisteredUnit) =>
    updateUnit(
      {
        nurseryId,
        fireAlarmCallPointId: unit.id,
        data: { isActive: !unit.isActive },
      },
      { onSuccess: () => toast.success("Call Point status updated") },
    );

  const handleSubmit = (values: RegisteredUnitFormValues) => {
    if (editingUnit) {
      updateUnit(
        {
          nurseryId,
          fireAlarmCallPointId: editingUnit.id,
          // No room picked means "not in a room", so an edit clears it.
          data: {
            ...values,
            roomId: values.roomId ?? null,
            notes: values.notes ?? null,
          },
        },
        {
          onSuccess: () => {
            toast.success("Call Point updated successfully");
            setDialogOpen(false);
            setEditingUnit(null);
          },
        },
      );
      return;
    }
    createUnit({ nurseryId, data: values });
  };

  return (
    <>
      <RegisteredUnitTable
        units={units}
        isLoading={isLoading}
        error={error}
        copy={COPY}
        onAdd={() => {
          setEditingUnit(null);
          setDialogOpen(true);
        }}
        onEdit={(unit) => {
          setEditingUnit(unit);
          setDialogOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />
      <RegisteredUnitFormDialog
        nurseryId={nurseryId}
        unit={editingUnit}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUnit(null);
        }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        copy={FORM_COPY}
      />
    </>
  );
}
